import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
interface FireParticlesProps {
  /** Hard-capped at 20 so the compositor never has too much to do. */
  count?: number;
  opacity?: number;
}
const COLORS = ['#e8631a', '#f2a93b', '#b8291a'];
const MAX_PARTICLES = 20;

/** Coarse low-end device check: few cores or little memory. */
function isLowPoweredDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    deviceMemory?: number;
  };
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 4) return true;
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4) {
    return true;
  }
  return false;
}
export function FireParticles({
  count = 16,
  opacity = 1
}: FireParticlesProps) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || isLowPoweredDevice()) return;

    // Mount embers after the critical render so they never delay first paint.
    const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 400));
    const handle = idle(() => setEnabled(true));
    return () => {
      const cancel = (window as any).cancelIdleCallback;
      if (cancel) cancel(handle);else clearTimeout(handle as number);
    };
  }, []);
  const embers = useMemo(() => {
    const total = Math.min(count, MAX_PARTICLES);
    return Array.from({
      length: total
    }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 2 + Math.random() * 4,
      duration: 7 + Math.random() * 8,
      delay: Math.random() * 10,
      color: COLORS[i % COLORS.length]
    }));
  }, [count]);
  if (!enabled) return null;
  return <Box aria-hidden="true" position="absolute" inset={0} overflow="hidden" pointerEvents="none" zIndex={1} opacity={opacity} sx={{
    contain: 'strict'
  }} data-id="element-2" data-mp="8l37i2pws46s">
      {embers.map(ember => <span key={ember.id} className="ember" style={{
      left: `${ember.left}%`,
      width: `${ember.size}px`,
      height: `${ember.size}px`,
      background: ember.color,
      animationDuration: `${ember.duration}s`,
      animationDelay: `${ember.delay}s`
    }} data-id="element-3" data-mp="2djt58jg4sj2a" />)}
    </Box>;
}